/**
 * Client-Side Logging Service
 * Replaces console.log with structured logging for production environments
 * Browser-compatible version for React application
 */

/**
 * Log levels
 */
const LOG_LEVELS = {
  ERROR: 0,
  WARN: 1,
  INFO: 2,
  DEBUG: 3
};

/**
 * Environment detection
 */
const isProduction = process.env.NODE_ENV === 'production';
const isDevelopment = process.env.NODE_ENV === 'development';

/**
 * Get configured log level from environment
 */
function getLogLevel() {
  const level = (process.env.REACT_APP_LOG_LEVEL || 'INFO').toUpperCase();
  return LOG_LEVELS[level] !== undefined ? LOG_LEVELS[level] : LOG_LEVELS.INFO;
}

/**
 * Format log message for structured logging
 */
function formatLogMessage(level, message, meta = {}) {
  const timestamp = new Date().toISOString();

  return {
    timestamp,
    level: level.toLowerCase(),
    message,
    ...meta,
    environment: isProduction ? 'production' : 'development',
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
    url: typeof window !== 'undefined' ? window.location.href : 'unknown'
  };
}

/**
 * Should log based on current log level
 */
function shouldLog(level) {
  const currentLevel = getLogLevel();
  const messageLevel = LOG_LEVELS[level.toUpperCase()];
  return messageLevel <= currentLevel;
}

/**
 * Core logging function
 */
function log(level, message, meta = {}) {
  if (!shouldLog(level)) return;

  const logData = formatLogMessage(level, message, meta);

  // In development, use console with colors and readable format
  if (isDevelopment) {
    const colors = {
      ERROR: 'color: #ff0000; font-weight: bold',
      WARN: 'color: #ff9800; font-weight: bold',
      INFO: 'color: #2196f3; font-weight: bold',
      DEBUG: 'color: #9e9e9e'
    };

    const style = colors[level.toUpperCase()] || '';

    // Use appropriate console method
    const consoleMethod = {
      ERROR: console.error,
      WARN: console.warn,
      INFO: console.info,
      DEBUG: console.log
    }[level.toUpperCase()] || console.log;

    consoleMethod.call(console, `%c[${level.toUpperCase()}]`, style, message, meta);
  } else {
    // In production, use structured JSON logging (can be sent to logging service)
    console.log(JSON.stringify(logData));

    // TODO: Send to external logging service (e.g., Logtail, Datadog, Sentry)
    // sendToLoggingService(logData);
  }
}

/**
 * Specific log level functions
 */
export function error(message, meta = {}) {
  log('ERROR', message, meta);
}

export function warn(message, meta = {}) {
  log('WARN', message, meta);
}

export function info(message, meta = {}) {
  log('INFO', message, meta);
}

export function debug(message, meta = {}) {
  log('DEBUG', message, meta);
}

/**
 * API request logging
 */
export function logApiRequest(method, url, statusCode, duration, err = null) {
  const meta = {
    method,
    url,
    statusCode,
    duration: `${duration}ms`
  };

  if (err) {
    error(`API Request Failed: ${method} ${url}`, { ...meta, error: err.message });
  } else if (statusCode >= 400) {
    warn(`API Request Error: ${method} ${url}`, meta);
  } else {
    debug(`API Request: ${method} ${url}`, meta);
  }
}

/**
 * Component lifecycle logging
 */
export function logComponentMount(componentName, props = {}) {
  debug(`Component mounted: ${componentName}`, {
    component: componentName,
    props: Object.keys(props)
  });
}

export function logComponentUnmount(componentName) {
  debug(`Component unmounted: ${componentName}`, {
    component: componentName
  });
}

export function logComponentError(componentName, err, errorInfo = {}) {
  error(`Component error: ${componentName}`, {
    component: componentName,
    error: err.message,
    stack: err.stack,
    ...errorInfo
  });
}

/**
 * User action logging
 */
export function logUserAction(action, details = {}) {
  const meta = {
    action,
    ...details,
    timestamp: new Date().toISOString()
  };

  info(`User action: ${action}`, meta);
}

/**
 * Navigation logging
 */
export function logNavigation(from, to) {
  debug(`Navigation: ${from} -> ${to}`, {
    from,
    to,
    timestamp: new Date().toISOString()
  });
}

/**
 * Performance logging
 */
export function logPerformance(operation, duration, details = {}) {
  const meta = {
    operation,
    duration: `${duration}ms`,
    ...details
  };

  if (duration > 3000) { // Slow operations (>3s)
    warn(`Slow operation detected: ${operation}`, meta);
  } else {
    debug(`Performance: ${operation}`, meta);
  }
}

/**
 * Form submission logging
 */
export function logFormSubmit(formName, success = true, err = null) {
  const meta = {
    form: formName,
    success,
    timestamp: new Date().toISOString()
  };

  if (!success && err) {
    error(`Form submission failed: ${formName}`, { ...meta, error: err.message });
  } else {
    info(`Form submitted: ${formName}`, meta);
  }
}

/**
 * Authentication event logging
 */
export function logAuthEvent(event, details = {}) {
  const meta = {
    event,
    ...details,
    timestamp: new Date().toISOString()
  };

  info(`Auth event: ${event}`, meta);
}

/**
 * Payment event logging
 */
export function logPaymentEvent(event, details = {}) {
  const meta = {
    event,
    // Don't log sensitive payment details
    amount: details.amount,
    currency: details.currency,
    timestamp: new Date().toISOString()
  };

  info(`Payment event: ${event}`, meta);
}

/**
 * WebSocket event logging
 */
export function logWebSocketEvent(event, details = {}) {
  const meta = {
    event,
    ...details,
    timestamp: new Date().toISOString()
  };

  debug(`WebSocket event: ${event}`, meta);
}

/**
 * File upload logging
 */
export function logFileUpload(fileName, fileSize, success = true, err = null) {
  const meta = {
    fileName,
    fileSize: `${(fileSize / 1024 / 1024).toFixed(2)}MB`,
    success,
    timestamp: new Date().toISOString()
  };

  if (!success && err) {
    error(`File upload failed: ${fileName}`, { ...meta, error: err.message });
  } else {
    info(`File uploaded: ${fileName}`, meta);
  }
}

/**
 * Image generation logging
 */
export function logImageGeneration(templateName, duration, success = true, err = null) {
  const meta = {
    template: templateName,
    duration: `${duration}ms`,
    success,
    timestamp: new Date().toISOString()
  };

  if (!success && err) {
    error(`Image generation failed: ${templateName}`, { ...meta, error: err.message });
  } else {
    info(`Image generated: ${templateName}`, meta);
  }
}

/**
 * Error boundary logging
 */
export function logErrorBoundary(err, errorInfo) {
  error('React Error Boundary caught error', {
    error: err.message,
    stack: err.stack,
    componentStack: errorInfo.componentStack,
    timestamp: new Date().toISOString()
  });
}

/**
 * Feature flag logging
 */
export function logFeatureFlag(flag, enabled, details = {}) {
  debug(`Feature flag: ${flag}`, {
    flag,
    enabled,
    ...details
  });
}

/**
 * Cache operation logging
 */
export function logCacheOperation(operation, key, hit = null) {
  const meta = {
    operation,
    key,
    ...(hit !== null && { hit })
  };

  debug(`Cache operation: ${operation}`, meta);
}

/**
 * Analytics event logging
 */
export function logAnalyticsEvent(eventName, properties = {}) {
  debug(`Analytics event: ${eventName}`, {
    event: eventName,
    properties,
    timestamp: new Date().toISOString()
  });
}

// Note: Only named exports are used (no default export) to avoid webpack tree-shaking issues
// All functions are already exported individually above

